import { useState, useCallback } from 'react';
import { openAIService } from 'api/services/OpenAIService';

export interface UsePerformanceTestOptions {
  /** Product specs key-value map sent to the API */
  specs: Record<string, string>;
}

export interface PerformanceTestState {
  loading: boolean;
  result: string | null;
  error: string | null;
}

const initialState: PerformanceTestState = {
  loading: false,
  result: null,
  error: null,
};

/**
 * Hook to run FPS (game) and software compatibility API tests from listing detail pages.
 * Handles loading and result/error state; APIs return plain string in service result data.
 */
export function usePerformanceTest({ specs }: UsePerformanceTestOptions) {
  const [fpsState, setFpsState] = useState<PerformanceTestState>(initialState);
  const [compatState, setCompatState] = useState<PerformanceTestState>(initialState);

  const runFpsTest = useCallback(
    async (game: string) => {
      if (!game?.trim()) return;
      setFpsState({ loading: true, result: null, error: null });
      try {
        const res = await openAIService.getFps({
          properties: specs,
          game: game.trim(),
        });
        if (res.isSuccess && res.data != null) {
          setFpsState({ loading: false, result: res.data, error: null });
        } else {
          setFpsState({
            loading: false,
            result: null,
            error: res.message ?? 'FPS analizi alınamadı.',
          });
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Bir hata oluştu.';
        setFpsState({ loading: false, result: null, error: message });
      }
    },
    [specs]
  );

  const runCompatibilityTest = useCallback(
    async (application: string) => {
      if (!application?.trim()) return;
      setCompatState({ loading: true, result: null, error: null });
      try {
        const res = await openAIService.getCompatibility({
          properties: specs,
          application: application.trim(),
        });
        if (res.isSuccess && res.data != null) {
          setCompatState({ loading: false, result: res.data, error: null });
        } else {
          setCompatState({
            loading: false,
            result: null,
            error: res.message ?? 'Uyumluluk analizi alınamadı.',
          });
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Bir hata oluştu.';
        setCompatState({ loading: false, result: null, error: message });
      }
    },
    [specs]
  );

  return {
    fps: { ...fpsState, run: runFpsTest },
    compatibility: { ...compatState, run: runCompatibilityTest },
  };
}
